"use client";

import { forwardRef, ForwardedRef } from "react";
import Link from "next/link";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Theme } from "@mui/material/styles";

import { useStyles } from "./styles";
import { ExploreRelatedImage } from "@/types/thread";
import useSafetyFilter from "@/hooks/useSafetyFilter";
import InpaintButton from "../../InpaintButton";
import CopyImage from "@/components/common/CopyImage";
import RatingControls from "@/components/social/ratings/RatingControls";
import Image from "@/components/common/Image";
import UserDetails from "@/components/profile/UserDetails";

interface GalleryImageProps {
  data: ExploreRelatedImage;
  onImageClick: (imageId: number) => void;
  ref?: ForwardedRef<HTMLDivElement>;
}

const GalleryImage = forwardRef<HTMLDivElement, GalleryImageProps>(
  ({ data, onImageClick }, ref) => {
    const { classes } = useStyles();
    const isMobile = useMediaQuery((theme: Theme) =>
      theme.breakpoints.down("sm")
    );
    const isUnsafe = data.images[0].safetyRating === 1;
    const blurImage = useSafetyFilter(isUnsafe);

    const url = data.slug
      ? `/gen/${data.slug}-${data.id}/?highlighted=${data.images[0].id}`
      : `/idea/${data.roomUUID}`;

    if (!data.images[0]?.url) return null;

    return (
      <Link
        key={data.id}
        href={url}
        onClick={() => !data.slug && onImageClick(data.id)}
        className={classes.link}
        prefetch={false}
      >
        <div className={classes.galleryImageContainer} ref={ref}>
          <div
            data-track-resource=""
            data-resource-id={data.images[0].url.split("/").pop()}
            data-resource-name="image"
          >
            <Image
              src={data.images[0].url}
              alt={data.images[0].alt || ""}
              width={isMobile ? 160 : 200}
              height={isMobile ? 160 : 200}
              isUnsafe={isUnsafe}
              className={classes.galleryImage}
            />
          </div>
          <p className={classes.title}>{data.title}</p>
          <div className={classes.controls}>
            {!blurImage && (
              <InpaintButton url={data.images[0].url} color="white" />
            )}
            {!blurImage && (
              <CopyImage imageUrl={data.images[0].url} color="white" />
            )}
            {!blurImage && (
              <RatingControls
                imageId={data.id}
                likesCount={data.images[0].likesCount || 0}
                userRating={data.images[0].userRating || null}
              />
            )}
          </div>
        </div>
        <UserDetails
          username={data.username || "Anonymous"}
          avatarUrl={data.avatarUrl}
          subscriptionType={data.subscriptionType}
          showAvatar
          orientation="vertical"
          avatarSize="small"
        />
      </Link>
    );
  }
);

export default GalleryImage;

GalleryImage.displayName = "GalleryImage";
